import { TableCell, Theme, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import React from 'react';

type CellStyleProps = {
  size: 'small' | 'medium';
  borderSize: number;
  capitalize?: boolean;
  verticalAlign: 'top' | 'middle';
};

type CellFeedStyleProps = {
  capitalize?: boolean;
};

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (props) =>
    props !== 'size' && props !== 'borderSize' && props !== 'borderColor' && props !== 'capitalize' && props !== 'verticalAlign',
})<CellStyleProps>(({ theme, size, capitalize, verticalAlign }) => ({
  padding: theme.spacing(2),
  fontSize: 14,
  lineHeight: 1.25,
  height: size === 'medium' ? 32 : 32,
  verticalAlign,
  [theme.breakpoints.up('sm')]: {
    textTransform: capitalize ? 'capitalize' : 'none',
  },
}));

const Cell = styled(TableCell)<CellFeedStyleProps>(({ theme, capitalize }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'stretch',
  padding: theme.spacing(1, 2),
  fontSize: 14,
  borderBottom: `1px solid ${theme.palette.darkBlue.main}`,
  lineHeight: 1.25,
  minHeight: 46,
  color: theme.palette.darkBlue.main,

  [theme.breakpoints.up('xs')]: {
    textTransform: capitalize ? 'capitalize' : 'none',
    whiteSpace: 'nowrap',
    alignItems: 'center',
  },
  '&:nth-last-of-type(1)': {
    borderBottom: 'none',
  },
}));

const CellLabel = styled('div', { label: 'MobileCellLabel' })(({ theme }) => ({
  fontWeight: 500,
  marginRight: '40px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
}));

const CellValue = styled('div', { label: 'MobileCellValue' })(({ theme }) => ({
  width: '100%',
  paddingBottom: '2px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end',
  textAlign: 'right',
  textWrap: 'balance',
  '&:nth-type-of(1)': {
    width: '100%',
    padding: theme.spacing(2, 0),
  },
}));

export type ListCellProps = {
  size?: 'small' | 'medium';
  align?: 'left' | 'right' | 'center';
  label?: string;
  colSpan?: number;
  rowSpan?: number;
  capitalize?: boolean;
  isMobile?: boolean;
  borderSize?: number;
  verticalAlign?: 'top' | 'middle';
  sx?: SxProps<Theme> | undefined;
  className?: string | undefined;
  children?: React.ReactNode;
  selected?: boolean;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  onMouseOver?: React.MouseEventHandler<HTMLElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLElement>;
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
};

const ListCell: React.FC<ListCellProps> = ({
  label,
  sx,
  capitalize = false,
  colSpan = 1,
  rowSpan = 1,
  isMobile = false,
  align = 'left',
  size = 'medium',
  borderSize = 7,
  verticalAlign = 'middle',
  children,
  ...rest
}) => {
  const theme = useTheme();

  const isInternalMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return isMobile || isInternalMobile ? (
    <Cell capitalize={capitalize} sx={sx}>
      {!!label && <CellLabel sx={sx}>{label}</CellLabel>}
      <CellValue {...rest}>{!!children ? children : '-'}</CellValue>
    </Cell>
  ) : (
    <StyledTableCell
      capitalize={capitalize}
      size={size}
      borderSize={borderSize}
      verticalAlign={verticalAlign}
      align={align}
      rowSpan={rowSpan}
      colSpan={colSpan}
      sx={sx}
      {...rest}>
      {children}
    </StyledTableCell>
  );
};

export default ListCell;
